/**
 * Bekannte Chat-Modelle, die im Modell-Select angeboten und serverseitig
 * gegen eine Allowlist geprüft werden.
 *
 * Aliase müssen in LiteLLM konfiguriert sein (z.B. via model_list).
 * Der Wert wird 1:1 als "model" an LiteLLM übergeben.
 */
export const KNOWN_CHAT_MODELS = [
  { value: 'mistral/mistral-large-latest', label: 'Mistral Large' },
  { value: 'mistral/mistral-small-latest', label: 'Mistral Small' },
  // Nur der live verifizierte Kimi-Alias bleibt sichtbar/waehlbar.
  // Infrablocker: Der Alias muss im produktiven LiteLLM (model_list) auf
  // `moonshot/kimi-k2.7-code` gemappt sein.
  { value: 'moonshot/kimi-k2.7-code', label: 'Kimi K2.7 Code' },
] as const

export type KnownChatModelValue = (typeof KNOWN_CHAT_MODELS)[number]['value']

/** Alle bekannten Modellwerte als Set für schnelle Prüfungen. */
export const KNOWN_CHAT_MODEL_VALUES: ReadonlySet<KnownChatModelValue> = new Set(
  KNOWN_CHAT_MODELS.map((m) => m.value),
)

/**
 * EU-only Default-Allowlist für Chat (EDIH-Vertrag §2.2): neue Workspaces
 * starten ausschliesslich mit EU-Modellen (Mistral). Moonshot bleibt in
 * KNOWN_CHAT_MODELS sichtbar, ist aber nur per explizitem Admin-Opt-in
 * zusaetzlich zur Provider-Policy-Freigabe erreichbar.
 */
export const EU_DEFAULT_CHAT_MODELS: readonly KnownChatModelValue[] = [
  'mistral/mistral-large-latest',
  'mistral/mistral-small-latest',
]

export type ModelProvider = 'mistral' | 'moonshot' | 'anthropic' | 'deepseek' | 'google' | 'openai'
export type ModelRegion = 'eu' | 'non-eu'

/**
 * Dedizierte Audio-Modelle (Voxtral 3, Mistral EU — verifiziert 2026-10-21 gegen
 * https://docs.mistral.ai/api/endpoint/audio und /models). Diese Konstanten
 * sind KEINE Chat-Wahlmoeglichkeiten (nicht in KNOWN_CHAT_MODELS); sie werden
 * ausschliesslich serverseitig fuer Transkription (STT) und Sprachausgabe
 * (TTS) verwendet.
 */
export const AUDIO_TRANSCRIPTION_MODELS = [
  'voxtral-small-transcribe-3-0',
  'voxtral-mini-transcribe-3-0',
] as const

export type AudioTranscriptionModel = (typeof AUDIO_TRANSCRIPTION_MODELS)[number]

/** Default-Transkriptionsmodell (Voxtral Small Transcribe 3, EU). */
export const DEFAULT_AUDIO_TRANSCRIPTION_MODEL: AudioTranscriptionModel =
  'voxtral-small-transcribe-3-0'

export const AUDIO_TTS_MODELS = ['voxtral-mini-tts-3-0'] as const

export type AudioTtsModel = (typeof AUDIO_TTS_MODELS)[number]

/** Default-TTS-Modell (Voxtral Mini TTS 3, EU). */
export const DEFAULT_AUDIO_TTS_MODEL: AudioTtsModel = 'voxtral-mini-tts-3-0'

/** Feste deutsche TTS-Stimme (Voxtral 3 Preset "Fiona", verifiziert 2026-10-21). */
export const AUDIO_TTS_DEFAULT_VOICE_ID = 'voice_481'

export interface ModelRegistryEntry {
  value: string
  provider: ModelProvider
  region: ModelRegion
  label: string
}

/**
 * Zentrales Modell-Registry mit Provider-/Regions-Metadaten (EDIH-Vertrag §2.1).
 * Deckt KNOWN_CHAT_MODELS, OUTPUT_ROUTING-Modelle, Image-Gen-Modelle und die
 * internen Assistenz-Modelle (Cleanup/Brand-Fallback) ab. Jeder LiteLLM-Call
 * wird serverseitig gegen dieses Registry geprueft — unbekannte Modellstrings
 * sind per Definition nicht erlaubt (fail-closed).
 */
export const MODEL_REGISTRY: readonly ModelRegistryEntry[] = [
  { value: 'mistral/mistral-large-latest', provider: 'mistral', region: 'eu', label: 'Mistral Large' },
  { value: 'mistral/mistral-small-latest', provider: 'mistral', region: 'eu', label: 'Mistral Small' },
  { value: 'mistral/mistral-medium-latest', provider: 'mistral', region: 'eu', label: 'Mistral Medium' },
  { value: 'moonshot/kimi-k2.7-code', provider: 'moonshot', region: 'non-eu', label: 'Kimi K2.7 Code' },
  { value: 'anthropic/claude-sonnet-4-6', provider: 'anthropic', region: 'non-eu', label: 'Claude Sonnet 4.6' },
  { value: 'deepseek-r1', provider: 'deepseek', region: 'non-eu', label: 'DeepSeek R1' },
  { value: 'openai/gpt-5.5', provider: 'openai', region: 'non-eu', label: 'GPT-5.5' },
  // whisper-1 bleibt ausschliesslich fuer historische llm_usage_log-Eintraege
  // registriert (non-eu, policy-blockiert). Aktive Transkription laeuft ueber
  // die EU-Voxtral-Modelle unten.
  { value: 'whisper-1', provider: 'openai', region: 'non-eu', label: 'Whisper (Audio-Transkription, historisch)' },
  // Voxtral 3 Audio-Modelle (Mistral EU, /models verifiziert 2026-10-21).
  { value: 'voxtral-small-transcribe-3-0', provider: 'mistral', region: 'eu', label: 'Voxtral Small Transcribe 3' },
  { value: 'voxtral-mini-transcribe-3-0', provider: 'mistral', region: 'eu', label: 'Voxtral Mini Transcribe 3' },
  { value: 'voxtral-mini-tts-3-0', provider: 'mistral', region: 'eu', label: 'Voxtral Mini TTS 3' },
  { value: 'gemini/gemini-3-pro-image-preview', provider: 'google', region: 'non-eu', label: 'Gemini 3 Pro Image' },
  { value: 'gemini/gemini-3.1-flash-image-preview', provider: 'google', region: 'non-eu', label: 'Gemini 3.1 Flash Image' },
]

const MODEL_REGISTRY_BY_VALUE: ReadonlyMap<string, ModelRegistryEntry> = new Map(
  MODEL_REGISTRY.map((entry) => [entry.value, entry]),
)

/** Liefert den Registry-Eintrag fuer einen LiteLLM-Modellstring oder null (unbekannt). */
export function getModelRegistryEntry(model: string): ModelRegistryEntry | null {
  return MODEL_REGISTRY_BY_VALUE.get(model) ?? null
}
